import React, { useEffect, useState, useCallback } from 'react';
import { Building2, Network, KeySquare, Users, TrendingUp, ShoppingBag, RefreshCw } from 'lucide-react';
import { useAdminAuth } from '../../context/AdminAuthContext';
import { getSummary, listTenants } from '../../utils/adminApi';

function StatCard({ icon: Icon, label, value, sub, color }) {
  return (
    <div className="admin-stat-card">
      <div className="admin-stat-icon" style={{ color }}>
        <Icon size={20} />
      </div>
      <div>
        <div className="admin-stat-value">{value ?? '—'}</div>
        <div className="admin-stat-label">{label}</div>
        {sub && <div className="admin-stat-sub">{sub}</div>}
      </div>
    </div>
  );
}

export default function OverviewPage() {
  const { adminKey }          = useAdminAuth();
  const [summary, setSummary] = useState(null);
  const [tenants, setTenants] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError]     = useState('');

  const load = useCallback(() => {
    setLoading(true);
    setError('');
    Promise.all([getSummary(adminKey), listTenants(adminKey)])
      .then(([s, t]) => { setSummary(s); setTenants(t); })
      .catch(err => setError(err.message))
      .finally(() => setLoading(false));
  }, [adminKey]);

  useEffect(() => { load(); }, [load]);

  if (error) return (
    <div className="admin-error-page">
      <p>{error}</p>
      <button className="admin-btn-secondary" onClick={load} style={{ marginTop: 12 }}>
        <RefreshCw size={13} /> Retry
      </button>
    </div>
  );

  return (
    <div className="admin-page">
      <div className="admin-page-header">
        <h2 className="admin-page-title">Overview</h2>
        <button className="admin-btn-icon" onClick={load} title="Refresh">
          <RefreshCw size={15} className={loading ? 'spin' : ''} />
        </button>
      </div>

      <div className="admin-stat-grid">
        <StatCard icon={Building2}  label="Total Tenants"       value={summary?.total_tenants}       color="var(--clr-accent)" />
        <StatCard icon={TrendingUp} label="Active Tenants"      value={summary?.active_tenants}      color="var(--clr-success)" />
        <StatCard icon={Network}    label="Channels"            value={summary?.total_channels}      color="var(--clr-info)" />
        <StatCard icon={KeySquare}  label="Active Licences"     value={summary?.active_licence_keys} color="var(--clr-warning)" />
        <StatCard icon={Users}      label="Total Users"         value={summary?.total_users}         color="var(--clr-pp)" />
        <StatCard
          icon={ShoppingBag}
          label="Direct / Reseller"
          value={summary ? `${summary.direct_tenants} / ${summary.reseller_tenants}` : '—'}
          sub="tenants by channel"
          color="var(--clr-fi)"
        />
      </div>

      <h3 className="admin-section-title">All Tenants</h3>
      <div className="admin-table-wrap">
        <table className="admin-table">
          <thead>
            <tr>
              <th>Tenant ID</th><th>Name</th><th>Channel</th>
              <th>Users</th><th>Licence Expiry</th><th>Status</th>
            </tr>
          </thead>
          <tbody>
            {loading && (
              <tr><td colSpan={6} className="admin-table-empty">Loading…</td></tr>
            )}
            {!loading && tenants.length === 0 && (
              <tr><td colSpan={6} className="admin-table-empty">No tenants yet</td></tr>
            )}
            {!loading && tenants.map(t => (
              <tr key={t.tenant_id}>
                <td><code>{t.tenant_id}</code></td>
                <td>{t.name}</td>
                <td><span className="admin-tag">{t.channel_id || 'direct'}</span></td>
                <td>{t.user_count} / {t.max_users}</td>
                <td>{t.licence_expiry || <span className="admin-muted">None</span>}</td>
                <td>
                  <span className={`admin-badge ${t.is_active ? 'active' : 'inactive'}`}>
                    {t.is_active ? 'Active' : 'Inactive'}
                  </span>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
